import PageSection from "@components/PageSection";
import { FlexBox, Text } from "@styles";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { GetFeaturedImage, GetThumbnailImage } from "@services/ProductImages";
import { CompareColorsByHueAndPrimaryLine, GetColorLineMap, ReduceProductListToFirstOccurrenceOfEachColor } from "@services/ProductList";
import { useAuth } from "@contexts/AuthContext";
import { CatalystPaletteSelector } from "@components/PaletteSelector";
import { Button } from "@components/Catalyst/button";
import BasicSearchInput from "../../BasicSearchInput";
import ColorInfoModal from "./ColorInfoModal";



const SubColorSwatch = styled.div`
  border: 0px;
  width: 95px;
  height: 48px;
  display: flex;
`;

interface IProps {
  colors: readonly Queries.ProductFragment[];
  brandCode: string;
  displayedColorLineKey?: string;
  showcolorLineFilter?: boolean;
}

const Colors = ({ colors, displayedColorLineKey, brandCode, showcolorLineFilter = false }: IProps) => {
  const { gacUser } = useAuth();

  const baseColors = [...colors].sort(CompareColorsByHueAndPrimaryLine);
  const colorsOnly = !(baseColors.filter((c) => !c.products_colors).length > 0);

  const productNumMap = useMemo(
    () =>
      colors.reduce((result: Map<string, Queries.ProductFragment>, value) => {
        value.products_colors?.color?.products_colors?.forEach((pc) => {
          if (pc.product?.brand?.code === brandCode && pc.product?.item_parents?.length > 0) {
            pc.product.item_parents.forEach((ip) => {
              if (ip.friendly_item_number && !result.has(ip.friendly_item_number)) result.set(ip.friendly_item_number, value);
            });
          }
        });
        return result;
      }, new Map<string, Queries.ProductFragment>()),
    [colors],
  );

  const colorLinesList = useMemo(() => {
    // grab the color lines, remove the colorLine we're trying to
    const colorLines = GetColorLineMap(baseColors);
    if (displayedColorLineKey) colorLines.delete(displayedColorLineKey);

    const baselines = [
      { label: colorsOnly ? "All Colors" : "All Products", value: "" },
      ...[...colorLines.entries()].map(([key, value]) => ({ label: value.name, value: key })),
    ];

    let userPalettes: { label: string; value: string }[] = [];
    if (gacUser?.palettes) {
      userPalettes = gacUser.palettes
        .filter((palette) => (palette?.colors ?? []).find((c) => productNumMap.has(c)))
        .map((palette) => ({ label: palette.paletteName, value: `Palette${palette.id}` }));
    }

    return [...baselines, ...userPalettes];
  }, [baseColors, displayedColorLineKey, colorsOnly]);

  const palettesForSelectOptions = useMemo(() => {
    const personalPalettes = gacUser?.palettes
      ? gacUser.palettes
          .filter((palette) => (palette?.colors ?? []).find((c) => productNumMap.has(c)))
          .map((palette) => ({ label: palette.paletteName, value: `Palette${palette.id}` }))
      : [];

    const colorLines = GetColorLineMap(baseColors);
    if (displayedColorLineKey) colorLines.delete(displayedColorLineKey);
    const goldenPalettes = [
      { label: colorsOnly ? "All Colors" : "All Products", value: "" },
      ...[...colorLines.entries()].map(([key, value]) => ({ label: value.name, value: key })),
    ];

    return [
      { label: "Personal Palettes", options: personalPalettes },
      { label: "Golden Palettes", options: goldenPalettes },
    ];
  }, [gacUser]);

  const [searchInput, setSearchInput] = useState("");
  const [lineSubFilter, setLineSubFilter] = useState("");
  const [showAllColors, setShowAllColors] = useState(false);
  const [modalColorIndex, setModalColorIndex] = useState<number | null>(null);

  const baseSearched = searchInput
    ? baseColors.filter((p) => {
        const colorName = p.products_colors?.color?.color_name ?? "";
        const itemNumbers = p.item_parents.map((ip) => ip.friendly_item_number).join(" ");
        return colorName.toLowerCase().includes(searchInput.toLowerCase()) || itemNumbers.toLowerCase().includes(searchInput.toLowerCase());
      })
    : baseColors;

  let filteredColors: Queries.ProductFragment[];

  if (lineSubFilter.startsWith("Palette")) {
    const palette = (gacUser?.palettes ?? []).find((p) => p.id === lineSubFilter.replace("Palette", ""));

    const toRet = palette?.colors
      .map((c) => productNumMap.get(c))
      .reduce((result: Queries.ProductFragment[], value) => {
        if (value) result.push(value);
        return result;
      }, []);

    filteredColors = baseSearched.filter((p) => toRet?.find((t) => t.slug === p.slug));
  } else if (lineSubFilter === "") {
    filteredColors = baseSearched.reduce(ReduceProductListToFirstOccurrenceOfEachColor, []);
  } else {
    filteredColors = baseSearched.filter((p) => p.product_groups.find((pg) => pg.id === lineSubFilter));
  }

  if (!filteredColors) return null;

  const limit = !showAllColors && filteredColors.length > 50 ? 40 : filteredColors.length;
  const limitedFilteredColors = !showAllColors ? filteredColors.slice(0, limit) : filteredColors;

  const closeModal = () => {
    setModalColorIndex(null);
  };

  const prevColor = () => {
    if (modalColorIndex != null) setModalColorIndex(modalColorIndex - 1);
  };

  const nextColor = () => {
    if (modalColorIndex != null) setModalColorIndex(modalColorIndex + 1);
  };

  return (
    <PageSection sectionName="Colors " subheading="(Click swatch for detail)">
      <FlexBox justifyContent="center" direction="column" alignItems="center" maxWidth={80} style={{ alignSelf: "center" }}>
        {showcolorLineFilter && colorLinesList.length > 0 && (
          <FlexBox direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <FlexBox alignItems="center">

              <CatalystPaletteSelector
                defaultValue={palettesForSelectOptions[1].options[0].value}
                groupedPaletteOptions={palettesForSelectOptions}
                onChange={(value : string) => {
                  setLineSubFilter(value ?? "");
                  setModalColorIndex(null);
                  setShowAllColors(false);
                }}
              />
            </FlexBox>

            <BasicSearchInput searchInput={searchInput} setSearchInput={(value) => setSearchInput(value)} placeholder="Search Colors" />
          </FlexBox>
        )}

        <FlexBox flexWrap justifyContent="center" gap={1.5} width="100%" style={{ alignItems: "space-between" }}>
          {limitedFilteredColors.length === 0 && <Text>No colors found</Text>}
          {limitedFilteredColors.map((p, i) => {
            const thumbnail = GetThumbnailImage(p);

            return (
              <a
                href={`/products/${p.slug}`}
                onClick={(e) => {
                  if (GetFeaturedImage([...p.external_images], p.product_name ?? "")) {
                    setModalColorIndex(i);
                    e.preventDefault();
                    return false;
                  }
                  return true;
                }}
                key={p.slug}
                className="flex cursor-pointer w-100px"
              >
                <div className="flex flex-col items-center w-24" >
                  {thumbnail?.fullImageUrl ? (
                    <img src={thumbnail.fullImageUrl} alt={thumbnail?.altText ?? ""} width="100px" />
                  ) : (
                    <SubColorSwatch
                      style={{
                        backgroundColor: `#${p?.products_colors?.color.hex_code}`,
                      }}
                    />
                  )}

                  <p className="text-center underline" >
                    {p?.products_colors?.color?.color_name ?? p?.product_name ?? ""}
                  </p>
                  <p className="text-sm  text-slate-700" >
                      {p.item_parents.map((ip) => `${ip.friendly_item_number} `)}{" "}
                  </p>
                </div>
              </a>
            );
          })}
        </FlexBox>
        {filteredColors.length !== limit && <Button onClick={() => setShowAllColors(true)}>See All</Button>}
      </FlexBox>
      {modalColorIndex != null && (
        <ColorInfoModal
          closeModal={closeModal}
          currentColor={limitedFilteredColors[modalColorIndex]}
          prevColor={
            modalColorIndex === 0 ||
            !GetFeaturedImage(
              [...limitedFilteredColors[modalColorIndex - 1].external_images],
              limitedFilteredColors[modalColorIndex - 1].product_name ?? "",
            )
              ? null
              : prevColor
          }
          nextColor={
            modalColorIndex === limitedFilteredColors.length - 1 ||
            !GetFeaturedImage(
              [...limitedFilteredColors[modalColorIndex + 1].external_images],
              limitedFilteredColors[modalColorIndex + 1].product_name ?? "",
            )
              ? null
              : nextColor
          }
        />
      )}
    </PageSection>
  );
};
export default Colors;
